/**
 * 섹션 지연 렌더링
 *
 * 첫 화면 밖 섹션을 첫 응답에서 빼고, 뷰포트에 다가올 때 Section Rendering API
 * (`?section_id=`) 로 받아 채운다. 서버가 첫 HTML 을 만드는 시간을 줄이는 것이 목적이다.
 *
 * 자리표시자는 snippets/section-lazy-placeholder.liquid 가 내보낸다.
 *
 * 삽입된 섹션을 살리는 방식 — 전역 브로드캐스트가 아니라 서브트리 직접 초기화다.
 *
 *   예전에는 `shopify:section:load` 를 bubbles:true 로 쏴서 테마의 초기화 모듈들을
 *   깨웠다. 그런데 그 이벤트는 원래 테마 에디터에서만 발화한다. 스토어프론트에서
 *   직접 쏘면 문서 전역을 재스캔하는 리스너들(global.js 의 게이트 밖 5개)이
 *   섹션을 채울 때마다 전부 깨어난다. 홈에서 최대 5회다.
 *
 *   그래서 이벤트를 쓰지 않고, 방금 삽입한 서브트리 안의 것만 직접 초기화한다.
 *   초기화가 필요한 것은 아래 initSection() 에 열거돼 있다. 지연 섹션이 다른
 *   모듈에 의존하게 되면 그 목록에 추가한다.
 */
(() => {
  const placeholders = document.querySelectorAll('[data-section-lazy]');
  if (!placeholders.length) return;

  const canObserve = 'IntersectionObserver' in window;

  /**
   * 삽입된 섹션 안의 컴포넌트를 초기화한다. 이 서브트리 밖은 건드리지 않는다.
   */
  const initSection = (section) => {
    // 슬라이더 — 자체 초기화 경로가 아직 돌지 않을 때만 구조한다.
    //
    // <swiper-component> 는 생성자에서 this.section = closest('section') 을 잡고,
    // 그게 .shopify-section 이면 IntersectionObserver 로 스스로 초기화한다 (global.js).
    // 이 섹션은 schema 의 "tag": "section" 덕에 Shopify 가 <section class="shopify-section">
    // 으로 감싸주므로 그 경로가 살아 있다. 교차 시점 초기화가 레이아웃이 잡힌 뒤에 돌아
    // 슬라이드 폭 계산이 정확하므로, 화면 밖에 꽂히는 경우에는 그대로 맡긴다.
    //
    // 직접 부르는 경우가 둘 있다.
    //
    // ① 이미 화면에 닿은 채로 꽂힐 때
    //    자체 경로는 섹션이 화면에 "실제로" 닿아야 도는데, 이쪽은 rootMargin 600px 로
    //    닿기 전에 미리 받아온다. 첫 화면에 가까운 섹션이 지연 대상이 되면 삽입은
    //    일찍 일어나고 실제 교차는 나중이라, 그 사이 슬라이더가 죽은 채로 남는다.
    //    홈 6번째 섹션을 대상에 넣었을 때 스크롤 직후 0/3 으로 관측됐다. 화면에 이미
    //    닿아 있다면 레이아웃도 잡혀 있으므로 기다릴 이유가 없다.
    //
    // ② 자체 경로가 통째로 막혔을 때
    //    래퍼 tag 가 div 인 섹션이 지연 대상이 되면 closest 가 null 이 되고 나머지 두
    //    경로(designMode 가드 안 / 이미 지나간 window load)도 막혀 영영 초기화되지 않는다.
    //
    // init 은 swiperInitialized 로 자체 가드를 하므로 자체 경로와 겹쳐 불려도 무해하다.
    const rect = section.getBoundingClientRect();
    const inView = rect.top < window.innerHeight && rect.bottom > 0;
    section.querySelectorAll('swiper-component').forEach((node) => {
      if (typeof node.init !== 'function') return;
      if (inView || !node.section) node.init();
    });

    // 탭 기본값. assets/tab-manager.js 가 노출한다. 이 섹션 안에서만 연다.
    if (typeof window.openDefaultTabs === 'function') {
      window.openDefaultTabs(section);
    }

    // 애니메이션.
    // base.css 에 `animation: fade-in ... paused` 가 걸려 있어 이 클래스가 붙지 않으면
    // 섹션이 투명한 채로 남는다. 이미 뷰포트에 닿아서 불러온 것이므로 바로 재생시킨다.
    if (/js-animation-/.test(section.className)) {
      section.classList.add('animation-init');
    }
    section.querySelectorAll('[class*="js-animation-"]').forEach((node) => {
      node.classList.add('animation-init');
    });

    // Yotpo 별점. 여기만 서브트리 한정이 아니라 문서 전역 재스캔이다.
    //
    // Yotpo 로더(layout/theme.liquid 의 async 스크립트)가 문서를 훑어 위젯을 그리는데,
    // 지연 섹션은 그 뒤에 들어오니 안에 있는 .yotpo-widget-instance 가 빈 채로 남는다.
    // 에러도 없고 화면도 멀쩡한데 별점만 안 뜬다.
    //
    // 위젯 자리가 있을 때만 부른다. 전역 재스캔이라 이 파일의 서브트리 원칙에서 벗어나므로
    // 필요한 때로 좁힌다.
    if (section.querySelector('.yotpo-widget-instance')) {
      reinitYotpo();
    }
  };

  /**
   * Yotpo 위젯을 다시 그린다. sections/ranking-collection.liquid 이 상품 목록을
   * 갈아끼울 때 쓰는 것과 같은 패턴이다.
   *
   * 두 번 부르는 이유 — 로더가 async 라 첫 호출 때 아직 안 와 있을 수 있다.
   * 그때는 두 전역이 다 없어 아무 일도 일어나지 않으므로, 늦게 오는 경우를 위해
   * 한 박자 뒤에 한 번 더 부른다. 이미 그려진 위젯을 다시 부르는 건 무해하다.
   *
   * 전역이 둘인 것도 로더 판올림 과정에서 이름이 갈린 탓이라 양쪽 다 확인한다.
   */
  const reinitYotpo = () => {
    const call = () => {
      try {
        if (window.yotpoWidgetsContainer && typeof window.yotpoWidgetsContainer.initWidgets === 'function') {
          window.yotpoWidgetsContainer.initWidgets();
        }
        if (window.yotpo && typeof window.yotpo.initWidgets === 'function') {
          window.yotpo.initWidgets();
        }
      } catch (e) {
        console.warn('[section-lazy] Yotpo 재초기화 실패:', e);
      }
    };

    window.requestAnimationFrame(call);
    window.setTimeout(call, 450);
  };

  /**
   * 응답 조각을 DOM 에 앉힌다.
   *
   * Section Rendering API 응답은 섹션 래퍼(<div id="shopify-section-…">)를 포함한다.
   * 자리표시자는 이미 그 래퍼 안에 들어 있으므로, 응답을 자리표시자 자리에 그대로
   * 넣으면 같은 id 를 가진 래퍼가 이중으로 겹친다. 그러면 `#shopify-section-X > div`
   * 패딩 규칙이 두 번 걸려 섹션 여백이 두 배가 되고, getElementById 도 바깥쪽을
   * 돌려준다. 응답이 래퍼를 포함하면 바깥 래퍼째 교체한다.
   */
  const mount = (placeholder, incoming) => {
    const wrapper = placeholder.closest('.shopify-section');
    const target =
      wrapper && incoming.classList.contains('shopify-section') ? wrapper : placeholder;

    target.replaceWith(incoming);
    return incoming;
  };

  const fill = (placeholder, attempt = 0) => {
    const id = placeholder.dataset.sectionLazy;
    const path = placeholder.dataset.sectionLazyPath || window.location.pathname;

    // 같은 오리진의 절대 경로만 허용한다. `//evil.com` 같은 프로토콜 상대 URL 을
    // 막는다 — 지금은 값의 출처가 routes.cart_url 뿐이라 성립하지 않지만,
    // 이 값이 외부에서 오게 되면 응답이 그대로 DOM 에 들어가므로 미리 닫아둔다.
    if (!/^\/(?!\/)/.test(path)) return;

    const url = `${path}?section_id=${encodeURIComponent(id)}`;

    const giveUp = () => {
      // 한 번 더 시도한 뒤에도 실패하면 자리표시자를 치운다.
      // 예약해 둔 높이가 사라지면서 레이아웃이 밀리므로, 재시도로 빈도를 줄인다.
      if (attempt === 0) {
        setTimeout(() => fill(placeholder, 1), 400);
        return;
      }
      placeholder.remove();
    };

    fetch(url, { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.text() : Promise.reject(res.status)))
      .then((html) => {
        const holder = document.createElement('div');
        holder.innerHTML = html.trim();
        const incoming = holder.firstElementChild;

        if (!incoming) {
          giveUp();
          return;
        }

        initSection(mount(placeholder, incoming));
      })
      .catch(giveUp);
  };

  // IntersectionObserver 가 없으면 지연하지 않고 즉시 모두 불러온다 (빈 화면 방지)
  if (!canObserve) {
    placeholders.forEach((el) => fill(el));
    return;
  }

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        obs.unobserve(entry.target);
        fill(entry.target);
      });
    },
    // 화면에 닿기 전에 미리 받아 스크롤 중 빈 칸이 보이지 않게 한다
    { rootMargin: '600px 0px' }
  );

  placeholders.forEach((el) => observer.observe(el));
})();
